import { getChatCompletion, tools } from '@worktango/ai-assistant/react';
import { fetchCurrentAccountQuery } from '~/behaviors/react-query/account';
import { coachCommentsQuery } from '~/behaviors/react-query/coachComments';
import { fetchFindSurveysQuery } from '~/behaviors/react-query/reporting';
import { resolvePeopleLeaderFilter } from './helpers/resolvePeopleLeaderFilter';

const DEFAULT_INSTRUCTIONS =
  'Summarize the comments and provide of the most relevant ones. Also highlight individual comments that are not part of the majority or group of comments.';

export const surveyCommentAnalysisTool = tools.createTool({
  name: 'survey_comment_analysis',
  description: `Analyze comments for one or more questions, based on one or more surveys, taking filters into account.

IMPORTANT: When the user wants to filter comments FOR a specific people leader/manager (i.e., attributeFilters includes the peopleLeaderAttributeId), check the users's message to see if they mention either:
- full hierarchy - all subordinates at all levels (If selected, set isHierarchyReport: true)
- direct reports only - (If selected, set isHierarchyReport: false)

If they did not mention either, you MUST ask them to choose full hierarchy or direct reports only so that you can determine how to set the isHierarchyReport parameter.

Filtering behavior:
- attributeFilters: Filters the comments to only include those from the specified attribute values (e.g., Department="Engineering")
- timeframe: ONLY continuous surveys (which collect responses on a rolling basis) can be filtered by a date range; it restricts the comments to those submitted within that range. Do NOT set timeframe for standard (non-continuous) surveys — they run for a single fixed period and are always analyzed over that whole period. If you are unsure whether the survey is continuous, call get_basic_survey_info first (it reports "Continuous Survey: Yes/No"). Omit timeframe to analyze the whole survey period.`,
  displayName: 'Analyzing survey comments',
  pastTenseDisplayName: 'Analyzed survey comments',
  /* istanbul ignore start -- parameter schema is not invoked in tests (tools.createTool is mocked) */
  parameters: (t) =>
    t.Object({
      surveys: t.Object({
        surveyIds: t.Optional(
          t.Array(t.Number({ description: 'The id of the survey to get the questions and comments from' })),
        ),
        surveyTitle: t.Optional(
          t.Array(
            t.String({ description: 'The title of the survey. It is required if the surveyId is not provided.' }),
          ),
        ),
      }),
      attributeFilters: t.Optional(
        t.Array(
          t.Object({
            id: t.Number({ description: 'Attribute ID to filter by' }),
            values: t.Array(t.String({ description: 'Attribute values to include' })),
          }),
          {
            description:
              'Optional filters to constrain the aggregate data analysis to specific attribute values (e.g., only data from Engineering department). If not supplied, overall aggregate data is returned, along with full attribute breakdowns by factor and question.',
          },
        ),
      ),
      peopleLeaderAttributeId: t.Optional(
        t.Number({
          description: 'The id of the attribute which contains the people leader ids',
        }),
      ),
      isHierarchyReport: t.Optional(
        t.Boolean({
          description:
            'REQUIRED when filtering by people leader/manager: Whether to include the full management hierarchy (true = all subordinates at all levels) or only direct reports (false = immediate reports only).',
        }),
      ),
      userInstructions: t.Optional(
        t.String({
          description: 'The instructions provided by the user on how to handle the results',
          default: DEFAULT_INSTRUCTIONS,
        }),
      ),
      timeframe: t.Optional(
        t.Object(
          {
            startTimeUTC: t.String({
              description:
                'Start of the range, inclusive, as an ISO 8601 UTC timestamp (e.g. "2026-01-01T00:00:00.000Z").',
            }),
            endTimeUTC: t.String({
              description:
                'End of the range, inclusive, as an ISO 8601 UTC timestamp (e.g. "2026-03-31T23:59:59.999Z").',
            }),
          },
          {
            description:
              'Optional time range, applicable ONLY to continuous surveys, that restricts the analysis to comments submitted within it. Omit it entirely for standard (non-continuous) surveys, and whenever the user has not asked to narrow the period. When the user asks for a relative range on a continuous survey (e.g. "last 30 days", "this quarter"), compute the concrete start/end from the current date in your context.',
          },
        ),
      ),
    }),
  /* istanbul ignore stop */
  fulfill: async (args) => {
    try {
      const { userInstructions, attributeFilters, surveys, peopleLeaderAttributeId, isHierarchyReport, timeframe } =
        args;

      // Check if manager/leader data is being used in any way
      const hasManagerFilter = attributeFilters?.some((filter) => filter.id === peopleLeaderAttributeId);
      const isUsingManagerData = peopleLeaderAttributeId !== undefined || hasManagerFilter;

      // Validate that when manager data is used, hierarchy choice is explicit
      if (isUsingManagerData && (isHierarchyReport === undefined || typeof isHierarchyReport !== 'boolean')) {
        return JSON.stringify({
          error: `Manager hierarchy data detected in filters. You MUST ask the user to specify:
- "Do you want direct reports only?" (set isHierarchyReport: false)
- "Do you want full management hierarchy?" (set isHierarchyReport: true)
Wait for their explicit choice before proceeding.`,
        });
      }

      const instructions = userInstructions ? userInstructions : DEFAULT_INSTRUCTIONS;

      // Timeframe filtering is only valid for continuous surveys, which requires resolving the
      // surveys by id. Without ids there is nothing to verify against.
      if (timeframe && (!surveys?.surveyIds || surveys.surveyIds.length === 0)) {
        return JSON.stringify({
          error: `To filter comments by a timeframe you must provide the numeric survey id(s), because only continuous surveys support timeframe filtering.`,
        });
      }

      const { account } = await fetchCurrentAccountQuery();
      if (surveys && surveys.surveyIds && surveys.surveyIds.length > 0) {
        const surveyDetails = await fetchFindSurveysQuery(surveys.surveyIds);
        const surveyTypeId = surveyDetails?.[0]?.types?.[0]?.id;
        for (const survey of surveyDetails) {
          const matchesOtherSurveyType = survey?.types?.find((type) => type.id === surveyTypeId);
          if (!matchesOtherSurveyType) {
            return JSON.stringify({
              error: `The surveys provided do not all belong to the same survey type. Tell the user to specify a survey type to analyze comments for.`,
            });
          }
        }

        // Standard surveys run for a single fixed period and cannot be sliced by date.
        if (timeframe) {
          const nonContinuousSurveyIds = surveyDetails
            .filter((survey) => !survey?.isContinuous)
            .map((survey) => survey.id);
          if (nonContinuousSurveyIds.length > 0) {
            return JSON.stringify({
              error: `A timeframe was provided, but survey(s) ${nonContinuousSurveyIds.join(', ')} are not continuous surveys. Only continuous surveys can be filtered by a date range. Retry without a timeframe to analyze the full survey period.`,
            });
          }
        }
      }

      const {
        managerReportIds,
        attributeFilters: filteredAttributes,
        error,
      } = await resolvePeopleLeaderFilter({
        companyId: account.companyId,
        languageId: account.langId,
        surveyIds: surveys.surveyIds || [],
        peopleLeaderAttributeId: peopleLeaderAttributeId ?? null,
        attributeFilters,
        isHierarchyReport,
      });

      // An unresolved name must stop the request. Dropping it would leave no people leader filter at
      // all, and the summary would then cover the whole survey while claiming to cover that leader.
      if (error) {
        return JSON.stringify({ error });
      }

      const commentsSummary = await summarizeComments({
        companyId: account.companyId,
        surveyIds: surveys.surveyIds || [],
        userInstructions: instructions,
        attributeFilters: filteredAttributes,
        managerReportIds: managerReportIds.length > 0 ? managerReportIds : undefined,
        isHierarchyReport: isHierarchyReport && managerReportIds.length > 0 ? isHierarchyReport : undefined,
        selectedTimeframe: timeframe,
      });

      return typeof commentsSummary === 'string' ? commentsSummary : JSON.stringify(commentsSummary);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return `Error: ${errorMessage}`;
    }
  },
});

async function summarizeComments({
  companyId,
  surveyIds,
  userInstructions,
  attributeFilters,
  managerReportIds,
  isHierarchyReport,
  selectedTimeframe,
}: {
  companyId: number;
  surveyIds: number[];
  userInstructions: string;
  attributeFilters: { id: number; values: string[] }[];
  managerReportIds?: string[];
  isHierarchyReport?: boolean;
  selectedTimeframe?: { startTimeUTC: string; endTimeUTC: string };
}) {
  const result: { coachComments: { comments: any[]; commentCount: number } } = await coachCommentsQuery({
    companyId,
    surveyIds,
    attributes: attributeFilters,
    managerReportIds,
    isHierarchyReport,
    selectedTimeframe,
  });
  const { comments, commentCount } = result.coachComments;

  if (commentCount === 0) {
    return {
      error: `No comments were found for the given parameters.`,
      summary: null,
    };
  }

  const compactComments = comments.map((comment) => ({
    text: comment.text,
  }));

  const commentsString = JSON.stringify(compactComments, null, 2);
  // TODO: Remove this if we really decide to go with the large model for all content.
  // const contentSize = commentsString.length;

  // const maxMediumModelTokens = 50_000;
  // const charsPerToken = 4;
  // const extraChars = 1; // extra chars to take into account things like spaces, new lines, etc.

  // const maxChars = maxMediumModelTokens * (charsPerToken + extraChars);

  const commentsSummary = await getChatCompletion({
    temperature: 0.25,
    model: 'medium',
    stream: false,
    systemInstruction: `
    You are a helpful assistant that summarizes comments.

    <instructions>
    ${userInstructions}
    </instructions>
    `,
    messages: [
      {
        role: 'user',
        parts: [
          {
            type: 'text',
            text: `Here are the comments.

          <comments>
          ${commentsString}
          </comments>
          `,
          },
        ],
      },
    ],
  });

  if (commentsSummary[0].type !== 'text') {
    return 'Error: The summary returned in an unexpected format. Please try again.';
  }

  return commentsSummary[0].text;
}
